import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useTransition } from "@/context/UIContext";
import { NAV, SERVICES } from "@/data/content";
import MobileMenu from "@/components/layout/MobileMenu";
import { MagneticButton } from "@/components/ui/Buttons";

function NavLabel({ label, active }) {
    return (
        <span className="relative z-10">
            {label}
            <span className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-[14px] rounded-full bg-electric opacity-0 shadow-glow transition-all duration-300 group-hover:translate-x-[-4px] group-hover:opacity-100" />
            <span className="absolute -bottom-1 left-1/2 h-px w-full -translate-x-1/2 origin-center scale-x-0 bg-electric/70 transition-transform duration-300 ease-premium group-hover:scale-x-100" />
            {active && (
                <motion.span
                    layoutId="nav-active-dot"
                    className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-electric shadow-glow"
                    data-testid="nav-active-indicator"
                />
            )}
        </span>
    );
}

function MegaMenu({ open, onNavigate, onMouseEnter, onMouseLeave }) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10, filter: "blur(10px)" }}
                    animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 0.97, y: 6, filter: "blur(8px)" }}
                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="services-mega-menu"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                    }}
                    className="absolute inset-x-0 top-full z-50 mx-auto mt-3 max-h-[calc(100dvh-110px)] w-[min(860px,calc(100%-24px))] overflow-y-auto overscroll-contain rounded-2xl border border-electric/15 bg-navy-900/95 shadow-card backdrop-blur-2xl"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0"
                        style={{ background: "radial-gradient(240px circle at var(--mx, 50%) var(--my, 0px), rgba(0,128,255,0.18), transparent 70%)" }}
                    />
                    <div className="relative grid md:grid-cols-[200px_minmax(0,1fr)]">
                        <div className="hidden flex-col justify-between border-r border-white/5 bg-navy-800/40 p-6 md:flex">
                            <div>
                                <div className="font-mono text-[10px] tracking-[0.3em] text-electric">SERVICES</div>
                                <p className="mt-3 text-sm leading-relaxed text-frost">Six disciplines. One outcome: growth.</p>
                            </div>
                            <button
                                onClick={() => onNavigate()}
                                data-testid="mega-view-all"
                                className="group flex items-center gap-2 text-xs font-medium text-ice transition-colors hover:text-electric"
                            >
                                View all services
                                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                        </div>
                        <div className="grid gap-1 p-4 sm:grid-cols-2">
                            {SERVICES.map((s, i) => (
                                <motion.button
                                    key={s.id}
                                    onClick={() => onNavigate(s.id)}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 + i * 0.05, duration: 0.3 }}
                                    data-testid={`mega-item-${s.id}`}
                                    data-cursor="button"
                                    className="group/item flex items-start gap-3 rounded-xl p-4 text-left transition-colors duration-300 hover:bg-electric/10"
                                >
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-white/10 bg-navy-800 text-electric transition-transform duration-300 group-hover/item:-rotate-6 group-hover/item:scale-110">
                                        <s.icon className="h-5 w-5" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="flex items-center gap-1.5 text-sm font-medium text-ice">
                                            {s.name}
                                            <ArrowUpRight className="h-3.5 w-3.5 text-electric opacity-0 transition-all duration-300 group-hover/item:translate-x-0.5 group-hover/item:-translate-y-0.5 group-hover/item:opacity-100" />
                                        </span>
                                        <span className="mt-1 block text-xs leading-relaxed text-frost">{s.short}</span>
                                    </span>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { go } = useTransition();
    const closeTimer = useRef();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const enterMega = () => {
        clearTimeout(closeTimer.current);
        setMegaOpen(true);
    };
    const leaveMega = () => {
        closeTimer.current = setTimeout(() => setMegaOpen(false), 180);
    };
    const megaNavigate = () => {
        setMegaOpen(false);
        go("/services", "OUR SERVICES");
    };

    return (
        <header className="fixed inset-x-0 top-0 z-[60] px-3 sm:px-5" data-testid="site-header">
            <motion.div
                animate={{
                    maxWidth: scrolled ? "1200px" : "100%",
                    height: scrolled ? 62 : 82,
                    marginTop: scrolled ? 14 : 0,
                    borderRadius: scrolled ? 22 : 0,
                    backgroundColor: scrolled ? "rgba(6,15,33,0.55)" : "rgba(6,15,33,0)",
                    boxShadow: scrolled
                        ? "0 18px 50px -20px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(0,128,255,0.18)"
                        : "0 0 0 0 rgba(0,0,0,0)",
                    backdropFilter: scrolled ? "blur(18px)" : "blur(0px)",
                }}
                transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
                className="relative mx-auto flex items-center justify-between px-5 md:px-8"
            >
                <motion.button
                    onClick={() => go("/", "ZENITHIX")}
                    data-testid="logo-button"
                    animate={{ scale: scrolled ? 0.88 : 1 }}
                    transition={{ duration: 0.4 }}
                >
                    <LogoFull />
                </motion.button>

                <nav className="hidden items-center gap-0.5 lg:flex" data-testid="desktop-nav">
                    {NAV.slice(0, 6).map((item) =>
                        item.path === "/services" ? (
                            <div key={item.path} onMouseEnter={enterMega} onMouseLeave={leaveMega}>
                                <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `group px-4 py-2.5 text-[13px] font-medium transition-colors duration-300 ${
                                            isActive || megaOpen ? "text-electric" : "text-ice/80 hover:text-electric"
                                        }`
                                    }
                                >
                                    <span className="flex items-center gap-1">
                                        <NavLabel label={item.label} active={false} />
                                        <ChevronDown
                                            className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`}
                                        />
                                    </span>
                                </NavLink>
                            </div>
                        ) : (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `group px-4 py-2.5 text-[13px] font-medium transition-colors duration-300 ${
                                        isActive ? "text-electric" : "text-ice/80 hover:text-electric"
                                    }`
                                }
                            >
                                {({ isActive }) => <NavLabel label={item.label} active={isActive} />}
                            </NavLink>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <MagneticButton to="/contact" testId="header-cta" className="hidden !h-10 !px-6 !text-xs lg:inline-flex">
                        LET'S TALK
                    </MagneticButton>
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        data-testid="menu-toggle"
                        aria-label="Toggle menu"
                        aria-expanded={menuOpen}
                        className="relative grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-navy-800/60 backdrop-blur lg:hidden"
                    >
                        <motion.span
                            animate={menuOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -3.5 }}
                            transition={{ duration: 0.3 }}
                            className="block h-px w-5 bg-ice"
                        />
                        <motion.span
                            animate={menuOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 3.5 }}
                            transition={{ duration: 0.3 }}
                            className="absolute h-px w-5 bg-ice"
                        />
                    </button>
                </div>
                <MegaMenu open={megaOpen} onNavigate={megaNavigate} onMouseEnter={enterMega} onMouseLeave={leaveMega} />
            </motion.div>

            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        </header>
    );
}
